<template>
  <pwa-layout subtitle="HISTORIQUE" :has-notif="true">
    <div class="pwa-history">

      <!-- Search + Date -->
      <div class="pwa-search-bar">
        <span class="mdi mdi-magnify pwa-search-icon"></span>
        <input
          v-model="search"
          class="pwa-search-input"
          placeholder="Rechercher (Chauffeur, bon...)"
          @input="debouncedLoad"
        />
        <button class="pwa-icon-btn-sm" @click="showDatePicker = !showDatePicker">
          <span class="mdi mdi-calendar-outline"></span>
        </button>
      </div>

      <!-- Date filters -->
      <div v-if="showDatePicker" class="pwa-date-row">
        <input type="date" v-model="startDate" class="pwa-date-input" @change="loadTx" />
        <span style="color:var(--pwa-muted);font-size:12px">→</span>
        <input type="date" v-model="endDate" class="pwa-date-input" @change="loadTx" />
        <button class="pwa-clear-btn" @click="startDate=null;endDate=null;loadTx()">
          <span class="mdi mdi-close"></span>
        </button>
      </div>

      <!-- Filter chips -->
      <div class="pwa-chips">
        <button
          v-for="f in filters"
          :key="f.key"
          class="pwa-chip"
          :class="{ 'pwa-chip--active': activeFilter === f.key }"
          @click="setFilter(f.key)"
        >
          <span v-if="f.icon" class="mdi" :class="f.icon" style="font-size:14px;margin-right:4px"></span>
          {{ f.label }}
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="pwa-loading">
        <span class="mdi mdi-loading mdi-spin"></span>
      </div>

      <!-- Grouped transactions -->
      <template v-else>
        <div v-for="group in groupedTx" :key="group.label">
          <div class="pwa-date-label">{{ group.label }}</div>
          <div
            v-for="tx in group.items"
            :key="tx.id"
            class="pwa-tx-card"
          >
            <div class="pwa-tx-icon" :class="txIconClass(tx)">
              <span class="mdi" :class="txIcon(tx)"></span>
            </div>
            <div class="pwa-tx-info">
              <div class="pwa-tx-title">{{ txTitle(tx) }}</div>
              <div class="pwa-tx-sub">
                {{ txSubtitle(tx) }}
                <span v-if="tx.shop"> · {{ tx.shop }}</span>
              </div>
            </div>
            <div class="pwa-tx-right">
              <span class="pwa-tx-amount" :class="{ 'pwa-tx-amount--pos': tx.amount > 0 }">
                {{ tx.amount > 0 ? '+' : '-' }} {{ formatAmount(Math.abs(tx.amount)) }} F
              </span>
              <span class="pwa-tx-time">{{ txTime(tx.created_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Infinite scroll sentinel -->
        <div ref="sentinel" style="height:1px;"></div>
        <div v-if="loadingMore" class="pwa-loading"><span class="mdi mdi-loading mdi-spin"></span></div>
        <div v-if="!loading && !loadingMore && !hasMore && transactions.length" class="pwa-end">Fin de la liste</div>
        <div v-if="!loading && !transactions.length" class="pwa-empty">Aucune transaction trouvée</div>
      </template>

    </div>
  </pwa-layout>
</template>

<script>
import PwaLayout from './PwaLayout.vue'
import TransactionDetailService from '@/services/TransactionDetailService'
import debounce from 'lodash/debounce'

export default {
  name: 'PwaHistory',
  components: { PwaLayout },
  data() {
    return {
      transactions: [],
      loading: false,
      loadingMore: false,
      hasMore: true,
      page: 1,
      search: '',
      startDate: null,
      endDate: null,
      showDatePicker: false,
      activeFilter: 'all',
      observer: null,
      filters: [
        { key: 'all', label: 'Tout' },
        { key: 'fuel', label: 'Carburant', icon: 'mdi-gas-station' },
        { key: 'voucher', label: 'Bons', icon: 'mdi-ticket-outline' },
      ],
    }
  },
  computed: {
    department() {
      return this.$store.state.app.project
    },
    groupedTx() {
      const groups = []
      const map = {}
      const today = new Date()
      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)
      const fmt = d => d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()
      const fmtKey = d => d.toISOString().slice(0, 10)

      this.transactions.forEach(tx => {
        const d = new Date(tx.created_at)
        let label
        if (fmtKey(d) === fmtKey(today)) label = "AUJOURD'HUI"
        else if (fmtKey(d) === fmtKey(yesterday)) label = 'HIER'
        else label = fmt(d)

        if (!map[label]) { map[label] = []; groups.push({ label, items: map[label] }) }
        map[label].push(tx)
      })
      return groups
    },
  },
  mounted() {
    this.loadTx()
    this.$nextTick(() => this.setupObserver())
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    debouncedLoad: debounce(function () { this.reset() }, 400),

    setFilter(key) {
      this.activeFilter = key
      this.reset()
    },

    params() {
      const p = { page: this.page, ordering: '-created_at', limit: 20 }
      if (this.search) p.search = this.search
      if (this.startDate) p.start_date = this.startDate
      if (this.endDate) p.end_date = this.endDate
      if (this.department?.id) p.department = this.department.id
      return p
    },

    async loadTx({ append = false } = {}) {
      if (append) this.loadingMore = true
      else this.loading = true
      try {
        const data = await TransactionDetailService.getAll(this, this.params())
        const inner = data?.results || data
        const results = Array.isArray(inner) ? inner : (inner?.results || [])
        this.transactions = append ? this.transactions.concat(results) : results
        this.hasMore = !!(data?.next || inner?.next)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => this.setupObserver())
      }
    },

    reset() {
      this.page = 1; this.hasMore = true; this.transactions = []
      this.loadTx()
    },

    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page++; this.loadTx({ append: true })
    },

    setupObserver() {
      if (this.observer) this.observer.disconnect()
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) this.loadMore() }, { threshold: 0.1 })
      this.observer.observe(el)
    },

    formatAmount(v) {
      return Number(v || 0).toLocaleString('fr-FR')
    },
    txTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
    },
    txTitle(tx) {
      const v = tx.user?.vehicles?.[0]
      if (v) return `${v.registration_number || ''} ${v.driver_last_name || ''}`.trim()
      return `${tx.user?.first_name || ''} ${tx.user?.last_name || ''}`.trim() || 'Transaction'
    },
    txSubtitle(tx) {
      const v = tx.user?.vehicles?.[0]
      if (v) return `${v.driver_first_name || ''} ${tx.product || ''}`.trim()
      return tx.product || ''
    },
    txIcon(tx) {
      if (tx.amount > 0) return 'mdi-credit-card-plus-outline'
      if (tx.product?.toLowerCase().includes('vidange') || tx.product?.toLowerCase().includes('maintenance')) return 'mdi-wrench'
      if (tx.voucher_used) return 'mdi-ticket-outline'
      return 'mdi-gas-station'
    },
    txIconClass(tx) {
      if (tx.amount > 0) return 'pwa-tx-icon--green'
      if (tx.voucher_used) return 'pwa-tx-icon--purple'
      if (tx.product?.toLowerCase().includes('vidange')) return 'pwa-tx-icon--orange'
      return 'pwa-tx-icon--blue'
    },
  },
}
</script>

<style scoped>
.pwa-history { padding: 16px 16px 0; }

/* Search */
.pwa-search-bar {
  display: flex;
  align-items: center;
  background: var(--pwa-white);
  border-radius: 12px;
  padding: 0 12px;
  height: 46px;
  gap: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  margin-bottom: 12px;
}
.pwa-search-icon { font-size: 18px; color: var(--pwa-muted); flex-shrink: 0; }
.pwa-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--pwa-text);
  background: transparent;
}
.pwa-search-input::placeholder { color: var(--pwa-muted); }
.pwa-icon-btn-sm {
  width: 32px; height: 32px;
  border: none; background: transparent;
  font-size: 18px; color: var(--pwa-muted);
  cursor: pointer; flex-shrink: 0;
}

/* Date row */
.pwa-date-row {
  display: flex; align-items: center; gap: 8px;
  background: var(--pwa-white); border-radius: 12px; padding: 8px 12px;
  margin-bottom: 12px;
}
.pwa-date-input {
  flex: 1; border: none; outline: none;
  font-size: 13px; color: var(--pwa-text); background: transparent;
}
.pwa-clear-btn {
  width: 28px; height: 28px; border-radius: 50%;
  border: none; background: #F0F4F8;
  font-size: 16px; color: var(--pwa-muted);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
}

/* Chips */
.pwa-chips {
  display: flex; gap: 8px; margin-bottom: 16px;
  overflow-x: auto; scrollbar-width: none;
}
.pwa-chips::-webkit-scrollbar { display: none; }
.pwa-chip {
  flex-shrink: 0; padding: 7px 16px; border-radius: 20px;
  border: 1.5px solid #E0E6ED; background: var(--pwa-white);
  font-size: 13px; font-weight: 500; color: var(--pwa-muted);
  cursor: pointer; display: flex; align-items: center;
  transition: all 0.2s;
}
.pwa-chip--active { background: var(--pwa-dark); border-color: var(--pwa-dark); color: white; }

/* Date label */
.pwa-date-label {
  font-size: 11px; font-weight: 600; color: var(--pwa-muted);
  letter-spacing: 0.8px; margin: 16px 0 8px;
}

/* TX card (shared) */
.pwa-tx-card {
  background: var(--pwa-white); border-radius: var(--pwa-card-radius);
  padding: 14px 16px; display: flex; align-items: center; gap: 12px;
  margin-bottom: 8px;
}
.pwa-tx-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
.pwa-tx-icon--blue { background: #E3F2FD; color: #1976D2; }
.pwa-tx-icon--orange { background: #FFF3E0; color: #E65100; }
.pwa-tx-icon--green { background: #E8F5E9; color: #388E3C; }
.pwa-tx-icon--purple { background: #F3E5F5; color: #7B1FA2; }
.pwa-tx-info { flex: 1; min-width: 0; }
.pwa-tx-title { font-size: 14px; font-weight: 600; color: var(--pwa-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pwa-tx-sub { font-size: 12px; color: var(--pwa-muted); margin-top: 2px; }
.pwa-tx-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
.pwa-tx-amount { font-size: 14px; font-weight: 700; color: var(--pwa-text); white-space: nowrap; }
.pwa-tx-amount--pos { color: var(--pwa-accent); }
.pwa-tx-time { font-size: 11px; color: var(--pwa-muted); }

.pwa-loading { text-align: center; padding: 24px; font-size: 24px; color: var(--pwa-muted); }
.pwa-empty { text-align: center; padding: 32px; color: var(--pwa-muted); font-size: 14px; }
.pwa-end { text-align: center; padding: 16px; color: var(--pwa-muted); font-size: 12px; }
</style>
